import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, FileText, ArrowRight, Mountain } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const statusPill = (s) => {
  const map = {
    ready:    { c: "#059669", bg: "#D1FAE5", t: "Pronto" },
    delivered:{ c: "#059669", bg: "#D1FAE5", t: "Entregue" },
    paid:     { c: "#B45309", bg: "#FEF3C7", t: "Pago · processando" },
    processing: { c: "#B45309", bg: "#FEF3C7", t: "Processando" },
    draft:    { c: "#64748b", bg: "#F1F5F9", t: "Aguardando pagamento" },
    expired:  { c: "#DC2626", bg: "#FEE2E2", t: "Expirado" },
    cancelled:{ c: "#64748b", bg: "#F1F5F9", t: "Cancelado" },
    failed:   { c: "#DC2626", bg: "#FEE2E2", t: "Falha" },
  };
  const m = map[s] || { c: "#64748b", bg: "#F1F5F9", t: s || "—" };
  return <span className="text-[10px] px-2 py-1 rounded-full font-semibold"
                style={{ color: m.c, background: m.bg }}>{m.t}</span>;
};

export default function MeusPedidosGuest() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/public/mineral/guest-orders/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Falha ao carregar"))
      .finally(() => setLoading(false));
  }, [token]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Informe seu e-mail"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/public/mineral/guest-magic-link`, { email: email.trim() });
      toast.success("Se este e-mail tiver relatórios, o link foi enviado.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar link");
    } finally { setSending(false); }
  };

  // === Sem token → tela para pedir magic link ===
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6"
           style={{ background: "linear-gradient(180deg,#050810 0%,#0B1B2F 100%)" }}
           data-testid="meus-pedidos-guest-request">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-4">
            <Mountain className="w-4 h-4" style={{ color: "#F0D37A" }}/>
            <span className="text-[11px] tracking-[.28em] uppercase font-semibold" style={{ color: "#F0D37A" }}>
              Hermes Mineral
            </span>
          </div>
          <h1 className="font-heading text-2xl text-white font-semibold mb-2">Meus relatórios</h1>
          <p className="text-neutral-300/70 text-sm mb-6 leading-relaxed">
            Informe o e-mail que você usou na compra e receberemos um link seguro para acessar todos os seus relatórios.
          </p>
          <form onSubmit={requestLink} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
              <input data-testid="input-guest-email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email" placeholder="seu@email.com"
                className="w-full h-12 pl-10 pr-4 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-amber-400/60"/>
            </div>
            <button type="submit" disabled={sending} data-testid="btn-send-magic-link"
              className="w-full h-12 rounded-xl font-semibold shadow-lg transition disabled:opacity-60"
              style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
              {sending ? "Enviando…" : "Enviar link de acesso"}
            </button>
          </form>
          <Link to="/consulta-mineral" className="mt-6 block text-center text-xs text-white/40 hover:text-white/70">
            Fazer nova consulta
          </Link>
        </div>
      </div>
    );
  }

  // === Com token → lista ===
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando…</div>;
  if (error)   return <div className="min-h-screen flex items-center justify-center px-6"><div className="max-w-md text-center text-rose-600" data-testid="guest-orders-error">{error}</div></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="meus-pedidos-guest-list">
      <div style={{ background: "linear-gradient(120deg,#050810 0%,#0B1B2F 100%)" }}>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-[11px] tracking-[.28em] uppercase font-semibold" style={{ color: "#F0D37A" }}>
            Hermes Mineral · Área do cliente
          </div>
          <h1 className="font-heading text-3xl text-white font-semibold mt-2">Seus relatórios</h1>
          <p className="text-neutral-300/70 text-sm mt-1">{data.email} · {data.orders.length} pedido{data.orders.length === 1 ? "" : "s"}</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {data.orders.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            Nenhum relatório encontrado para este e-mail.
          </div>
        ) : (
          <div className="space-y-3">
            {data.orders.map(o => (
              <div key={o.order_id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-4 justify-between shadow-sm hover:shadow-md transition"
                   data-testid={`guest-order-${o.order_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-slate-400"/>
                    <span className="text-sm font-semibold text-slate-900 truncate">{o.processo || o.order_id}</span>
                    {statusPill(o.status)}
                  </div>
                  <div className="text-xs text-slate-500">Pedido {o.order_id} · {formatDate(o.created_at)}</div>
                  {o.expires_at && (
                    <div className="text-[11px] text-amber-700 mt-1">Disponível até {formatDate(o.expires_at)}</div>
                  )}
                </div>
                {o.share_token ? (
                  <Link to={`/consulta-mineral/relatorio/${o.share_token}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl font-semibold text-sm"
                        style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
                    Abrir <ArrowRight className="w-3.5 h-3.5"/>
                  </Link>
                ) : (
                  <span className="text-xs text-slate-400">Aguardando processamento</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
